import { useEffect, useMemo, useState } from 'react';
import { apiGet, apiGetAll } from '../api';
import { arOf, byClient, byYear, timeByCode, timeByEmployee, wipMetrics } from '../utils/rollup';
import { filterByDateRange, isRangeActive } from '../utils/dateRange';

/**
 * Load and aggregate the financial picture for a SET of clients.
 *
 * One client id => a client roll-up. Every id in a group => the group roll-up.
 * The caller decides which; this hook does not know what a group is.
 *
 * Draft time and draft invoices are excluded from every returned figure. That
 * happens inside utils/rollup.js, not here — see COUNTED_TIME_STATUSES and
 * isIssued.
 *
 * INVARIANT: `partial` is true if ANY fetch failed — a client-metadata fetch
 * OR a money-bearing fetch (time-entries, invoices, write-offs). There is no
 * such thing as a "safe" fetch to swallow here: money-bearing failures are
 * strictly worse, since they silently understate billable time, WIP, and A/R
 * while still reporting `loading: false` with no error. A caller that renders
 * a dollar figure from this hook MUST NOT present it as complete when
 * `partial` is true — surface it (e.g. "incomplete data") instead.
 *
 * A failed client fetch is NOT silently dropped: it stays out of `clients` /
 * `clientsById` (so byYear() can't derive a fiscal year for that client's
 * write-offs and routes them to the explicit "Unknown" bucket — see
 * utils/rollup.js), but the id is reported back via `failedClientIds`.
 *
 * A failed money-bearing fetch is likewise NOT silently dropped: whatever
 * data DID load for the other ids/endpoints is still returned (nothing hangs
 * or blanks), but the failing (endpoint, client id) pairs are reported back
 * via `failedFetches` so a page can say which clients/datasets are missing
 * rather than quietly under-reporting.
 *
 * DATE RANGE (optional second argument, `{ startDate, endDate }`, ISO strings,
 * either may be blank = open-ended): every record is kept or dropped by its
 * OWN `date` -- time entry, invoice and write-off alike -- before any figure is
 * computed, so the tiles, Summary by Year, pies, group breakdown and the
 * tables all describe the same window. WIP in a window is therefore
 * time worked in it - invoiced in it - written off in it (the net change in
 * WIP over the period); for "since last invoiced" that is the unbilled time
 * accrued since the last bill. The ONE exception is A/R (`metrics.arBalance`):
 * what the client owes is a balance as of today, not activity in a window, so
 * it stays all-time. `allTime` carries the unfiltered rows and year rows so a
 * caller can drill into A/R (and find the last invoice) consistently.
 *
 * Both bounds blank returns exactly what this hook returned before ranges
 * existed (same arrays, same figures).
 */
export default function useClientRollup(clientIds, range = {}) {
  const startDate = range.startDate || '';
  const endDate = range.endDate || '';
  const [clients, setClients] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [writeOffs, setWriteOffs] = useState([]);
  const [failedClientIds, setFailedClientIds] = useState([]);
  const [failedFetches, setFailedFetches] = useState({
    timeEntries: [], invoices: [], writeOffs: [],
  });
  const [loading, setLoading] = useState(true);

  // Stable across renders when the ids are the same, so the effect below does not
  // re-fire on every parent render just because the array identity changed.
  const key = useMemo(
    () => (clientIds || []).filter(Boolean).map(Number).sort((a, b) => a - b).join(','),
    [clientIds],
  );

  useEffect(() => {
    if (!key) {
      setClients([]); setTimeEntries([]); setInvoices([]); setWriteOffs([]);
      setFailedClientIds([]);
      setFailedFetches({ timeEntries: [], invoices: [], writeOffs: [] });
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const ids = key.split(',').map(Number);
    setLoading(true);

    // Each id's fetch is tagged with its id and success/failure, same as
    // fetchClients below, so a failure can be reported via `failedFetches`
    // rather than silently collapsing that client's contribution to [].
    const forEachId = (endpoint) =>
      Promise.all(
        ids.map((id) =>
          apiGetAll(`${endpoint}?client=${id}`)
            .then((data) => ({ id, data, ok: true }))
            .catch(() => ({ id, data: [], ok: false })),
        ),
      ).then((results) => ({
        data: results.filter((r) => r.ok).flatMap((r) => r.data),
        failedIds: results.filter((r) => !r.ok).map((r) => r.id),
      }));

    // Each client fetch is tagged with its id and success/failure rather than
    // being reduced to null-on-error: a failure has to be reportable, not
    // just absent, so the caller can tell "no client with this id" apart from
    // "the fetch for this id failed".
    const fetchClients = () =>
      Promise.all(
        ids.map((id) =>
          apiGet(`/api/clients/${id}/`)
            .then((client) => ({ id, client, ok: true }))
            .catch(() => ({ id, client: null, ok: false })),
        ),
      );

    Promise.all([
      fetchClients(),
      forEachId('/api/time-entries/'),
      forEachId('/api/invoices/'),
      forEachId('/api/write-offs/'),
    ]).then(([clientResults, te, inv, wo]) => {
      if (cancelled) return;
      setClients(clientResults.filter((r) => r.ok).map((r) => r.client));
      setFailedClientIds(clientResults.filter((r) => !r.ok).map((r) => r.id));
      setTimeEntries(te.data);
      setInvoices(inv.data);
      setWriteOffs(wo.data);
      setFailedFetches({
        timeEntries: te.failedIds,
        invoices: inv.failedIds,
        writeOffs: wo.failedIds,
      });
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [key]);

  const clientsById = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])),
    [clients],
  );

  const allTimeYearRows = useMemo(
    () => byYear({ timeEntries, invoices, writeOffs, clientsById }),
    [timeEntries, invoices, writeOffs, clientsById],
  );
  const allTimeAr = useMemo(
    () => (invoices || []).reduce((s, i) => s + arOf(i), 0),
    [invoices],
  );

  const rangeActive = isRangeActive(startDate, endDate);
  const allTime = useMemo(
    () => ({ timeEntries, invoices, writeOffs, yearRows: allTimeYearRows }),
    [timeEntries, invoices, writeOffs, allTimeYearRows],
  );

  // From here down, timeEntries / invoices / writeOffs are the WINDOWED rows.
  const rangedTime = useMemo(
    () => filterByDateRange(allTime.timeEntries, startDate, endDate),
    [allTime, startDate, endDate],
  );
  const rangedInvoices = useMemo(
    () => filterByDateRange(allTime.invoices, startDate, endDate),
    [allTime, startDate, endDate],
  );
  const rangedWriteOffs = useMemo(
    () => filterByDateRange(allTime.writeOffs, startDate, endDate),
    [allTime, startDate, endDate],
  );

  const metrics = useMemo(
    () => ({
      ...wipMetrics({
        timeEntries: rangedTime, invoices: rangedInvoices, writeOffs: rangedWriteOffs, clientsById,
      }),
      arBalance: allTimeAr,
    }),
    [rangedTime, rangedInvoices, rangedWriteOffs, clientsById, allTimeAr],
  );
  const rangedYearRows = useMemo(
    () => (rangeActive
      ? byYear({ timeEntries: rangedTime, invoices: rangedInvoices, writeOffs: rangedWriteOffs, clientsById })
      : allTimeYearRows),
    [rangeActive, rangedTime, rangedInvoices, rangedWriteOffs, clientsById, allTimeYearRows],
  );
  const clientRows = useMemo(
    () => byClient({
      clients, timeEntries: rangedTime, invoices: rangedInvoices, writeOffs: rangedWriteOffs, clientsById,
    }),
    [clients, rangedTime, rangedInvoices, rangedWriteOffs, clientsById],
  );
  const employeeData = useMemo(() => timeByEmployee(rangedTime), [rangedTime]);
  const codeData = useMemo(() => timeByCode(rangedTime), [rangedTime]);

  const partial = failedClientIds.length > 0
    || failedFetches.timeEntries.length > 0
    || failedFetches.invoices.length > 0
    || failedFetches.writeOffs.length > 0;

  return {
    loading,
    // True whenever ANY fetch failed: a client, or a money-bearing fetch
    // (time-entries, invoices, write-offs) for any requested client. The
    // roll-up totals below are still returned (nothing hangs, throws, or
    // gets blanked) — but a caller MUST NOT present those totals as complete
    // when `partial` is true. See `failedClientIds` / `failedFetches` for
    // which clients/datasets are missing. Any write-offs owned by a failed
    // client will also have landed in byYear()'s "Unknown" (year: null)
    // bucket rather than their real year.
    partial,
    failedClientIds,
    failedFetches,
    clients,
    clientsById,
    // Windowed by the date range (identical to allTime.* when there is none).
    timeEntries: rangedTime,
    invoices: rangedInvoices,
    writeOffs: rangedWriteOffs,
    metrics,
    yearRows: rangedYearRows,
    rangeActive,
    allTime,
    clientRows,
    employeeData,
    codeData,
  };
}
